import {
  MapPin,
  ShieldEllipsis,
  User,
  Users,
  ShoppingCart,
  Check,
} from "lucide-react";
import React, { useState } from "react";
import { getAddonByTypeId } from "../api/addons.js";
import { useNavigate } from "react-router-dom";
import { addToCart } from "../utils/cart.js";
import { useTranslation } from "react-i18next";

const MultiLocations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const addonData = getAddonByTypeId(3);
  const durationOptions = [
    { label: "3 Months", price: 37500, nbr: 3 },
    { label: "6 Months", price: 75000, nbr: 6 },
    { label: "9 Months", price: 112500, nbr: 9 },
    { label: "12 Months", price: 150000, nbr: 12 },
  ];

  const defaultDuration = durationOptions[0];

  const [quantity, setQuantity] = useState(1);

  const [selectedDuration, setSelectedDuration] = useState(defaultDuration);

  const [message, setMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const calculateTotal = () => {
    return selectedDuration ? selectedDuration.price * quantity : 0;
  };

  const handleAddToPlan = () => {
    const cartItem = {
      addonTypeId: 3,
      addonType: "merchant_addon",
      duration_month: selectedDuration?.nbr,
      addonName: addonData?.name || "Multiple Branch Control",
      quantity: quantity,
      price: calculateTotal(),
      isOnetime: false,
    };

    localStorage.removeItem("cartItem");

    localStorage.setItem("cartItem", JSON.stringify(cartItem));

    addToCart(cartItem);

    setIsSuccess(true);
    setMessage(
      t("addedToCartMessage", {
        quantity: quantity,
      }),
    );

    setTimeout(() => {
      navigate("/PaymentTransitions");
    }, 1000);
  };

  if (!addonData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>{t("loadingAddonData")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="container">
        <div id="hero-section" className="py-20 px-5 bg-[#D9D9D9]">
          <div className="mx-auto w-1/2 space-y-3">
            <h1 className="text-3xl font-bold">{addonData.name}</h1>
            <h3 className="text-xl font-semibold">{addonData.description}</h3>
            <p className="space-x-1">
              <b className="text-secondary-color">{t("subscription")}</b>
              <b className="text-primary-color">{t("lifetime")}</b>
              <b className="text-secondary-color">{t("userSlot")}</b>
              <b className="text-primary-color">{t("reassign")}</b>
              <b className="text-secondary-color">{t("anytime")}</b>
            </p>
            <div className="py-1 flex gap-3 items-center border px-3">
              <span className="text-lg font-medium">{t("selectDuration")}</span>
              <select
                name=""
                id=""
                value={selectedDuration?.label || ""}
                onChange={(e) => {
                  const selected = durationOptions.find(
                    (opt) => opt.label === e.target.value,
                  );
                  setSelectedDuration(selected || null);
                }}
                className="border px-2 py-1"
              >
                {durationOptions.map((opt) => (
                  <option value={opt.label} key={opt.label}>
                    {opt.label}: {opt.price.toLocaleString()} MMk
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4 py-3">
              <span className="text-lg font-medium">{t("quantity")}</span>
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-3 py-2 hover:bg-gray-100 rounded-l-lg transition-colors"
                  disabled={quantity <= 1}
                >
                  <User className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 font-semibold min-w-[50px] text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-3 py-2 hover:bg-gray-100 rounded-r-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                </button>
              </div>
              <span className="text-lg font-bold text-primary-color">
                {t("total")}: {calculateTotal().toLocaleString()} MMK
              </span>
            </div>
            {isSuccess && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                <Check className="w-5 h-5" />
                <span className="font-medium">{message}</span>
              </div>
            )}
            <button
              onClick={handleAddToPlan}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all bg-blue-600 hover:bg-blue-700 text-white"
            >
              <ShoppingCart className="w-5 h-5" />
              {t("addToMyPlan")}
            </button>
          </div>
        </div>

        <div
          id="content"
          className="text-white text-center px-10 py-8"
          style={{ background: "linear-gradient(to top, #FFFAFA, #273469)" }}
        >
          <div className="w-1/2 flex flex-col mx-auto space-y-3">
            <h3 className="text-xl font-bold mb-3">
              {t("empowerTeamWithAccess")}
            </h3>
            <p>{t("empowerTeamDescription1")}</p>
            <p>{t("empowerTeamDescription2")}</p>
          </div>
        </div>

        <div id="body">
          <div className="grid grid-cols-4 gap-5 m-5 pt-5">
            <div className="bg-gray-100 rounded-xl space-y-3 text-center px-3 py-3">
              <div className="flex items-center justify-center text-orange-500">
                <User className="h-10 w-10" />
              </div>
              <h3 className="text-md font-bold">{t("lifetimeUserSlot")}</h3>
              <span className="text-gray-500 text-sm">
                {t("lifetimeUserSlotDesc")}
              </span>
            </div>
            <div className="bg-gray-100 rounded-xl space-y-3 text-center px-3 py-3">
              <div className="flex items-center justify-center">
                <MapPin className="h-10 w-10" />
              </div>
              <h3 className="text-md font-bold">
                {t("multiBranchAssignment")}
              </h3>
              <span className="text-gray-500 text-sm">
                {t("multiBranchAssignmentDesc")}
              </span>
            </div>
            <div className="bg-gray-100 rounded-xl space-y-3 text-center px-3 py-3">
              <div className="flex items-center justify-center">
                <Users className="h-10 w-10" />
              </div>
              <h3 className="text-md font-bold">
                {t("smoothTeamCollaboration")}
              </h3>
              <span className="text-gray-500 text-sm">
                {t("smoothTeamCollaborationDesc")}
              </span>
            </div>
            <div className="bg-gray-100 rounded-xl space-y-3 text-center px-3 py-3">
              <div className="flex items-center justify-center">
                <ShieldEllipsis className="h-10 w-10" />
              </div>
              <h3 className="text-md font-bold">{t("moreSecurity")}</h3>
              <span className="text-gray-500 text-sm">
                {t("moreSecurityDesc")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiLocations;
